import { useEffect, useState } from "react";
import type { AppProps } from "next/app";
import "@/styles/akasa.css";
import "@/styles/akasaMobile.css";
import "@/styles/maintainance.css";
import "@/styles/tailwind-reset.css";
import "@/styles/tailwind.css";
import { ApolloProvider } from "@apollo/client";
import { CacheProvider, EmotionCache } from "@emotion/react";
import { Provider as ReduxProvider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { InternetNotFoundPage } from "../components/InternetNotFound/InternetNotFound";
import { AuthContextProvider } from "../context/AuthContext";
import BookingDataContextProvider from "../context/BookingDataContext";
import BookingDetailsContextProvider from "../context/BookingDetailsContext";
import CRMContextProvider from "../context/CRMContext";
import CommonDataContextProvider from "../context/CommonDataContext";
import ManageBookingContextProvider from "../context/ManageBookingContext";
import { MuiThemeOverideProvider } from "../context/MuiThemeOverideProvider";
import { ScreenContextProvider } from "../context/ScreenContext";
import apolloClient from "../graphql";
import dynamic from "next/dynamic";
import { store } from "../store";
import { createEmotionCache } from "../utils/createEmotionCache";

const clientSideEmotionCache = createEmotionCache();
const persistor = persistStore(store);

export interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
}

function MyApp({ Component, emotionCache = clientSideEmotionCache, pageProps }: MyAppProps) {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(window.navigator.onLine);

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, []);

  return (
    <CacheProvider value={emotionCache}>
      <ApolloProvider client={apolloClient}>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <CommonDataContextProvider>
              <AuthContextProvider>
                <ScreenContextProvider>
                  <BookingDataContextProvider>
                    <ManageBookingContextProvider>
                      <BookingDetailsContextProvider>
                        <CRMContextProvider>
                          <MuiThemeOverideProvider>
                          {isOnline ? <Component {...pageProps} /> : <InternetNotFoundPage />}
                          </MuiThemeOverideProvider>
                        </CRMContextProvider>
                      </BookingDetailsContextProvider>
                    </ManageBookingContextProvider>
                  </BookingDataContextProvider>
                </ScreenContextProvider>
              </AuthContextProvider>
            </CommonDataContextProvider>
          </PersistGate>
        </ReduxProvider>
      </ApolloProvider>
    </CacheProvider>
  );
}

export default MyApp;
